import type { DealResourcePlan, ResourcingOutput } from '@protoss/shared';
import { formatCurrencyAmount, readRolePricing, resolveRoleCost } from '@protoss/shared';
import { GlobeIcon, HomeIcon, PieChartIcon } from '@protoss/ui';

export interface ResourcingModelSummaryProps {
  resourcing: ResourcingOutput;
  resourcePlan: DealResourcePlan | null;
  discountPercentageOverride?: number | null;
  defaultDiscountPercentage?: number | null;
}

function formatCurrency(value: number): string {
  return `$${formatCurrencyAmount(value)}`;
}

export function ResourcingModelSummary({
  resourcing,
  resourcePlan,
  discountPercentageOverride,
  defaultDiscountPercentage,
}: ResourcingModelSummaryProps) {
  // Bucket each role by its selected basis (mutually exclusive - a hybrid role
  // gets its own bucket with full hours + blended cost, not split across the
  // onshore/offshore buckets).
  let onshoreHours = resourcing.totals.total_hours; // Default to all onshore
  let onshoreCost = resourcing.totals.total_cost.onshore;
  let offshoreHours = 0;
  let offshoreCost = 0;
  let hybridHours = 0;
  let hybridCost = 0;

  if (resourcePlan?.status === 'generated') {
    onshoreHours = 0;
    onshoreCost = 0;
    offshoreHours = 0;
    offshoreCost = 0;
    for (const entry of resourcePlan.resourcingSnapshot.staffing_plan) {
      const { basis, offshorePercent } = readRolePricing(
        resourcePlan.rolePricingBasisByRoleId,
        entry.role_id
      );
      const cost = resolveRoleCost(entry, basis, offshorePercent);
      if (basis === 'hybrid') {
        hybridHours += entry.hours;
        hybridCost += cost;
      } else if (basis === 'offshore') {
        offshoreHours += entry.hours;
        offshoreCost += cost;
      } else {
        onshoreHours += entry.hours;
        onshoreCost += cost;
      }
    }
  }

  const effectiveDiscountPct =
    discountPercentageOverride ??
    defaultDiscountPercentage ??
    resourcePlan?.resourcingSnapshot?.totals?.discount?.percentage ??
    0;

  const discountMultiplier = 1 - effectiveDiscountPct / 100;
  onshoreCost *= discountMultiplier;
  offshoreCost *= discountMultiplier;
  hybridCost *= discountMultiplier;

  const cards = [];

  if (onshoreHours > 0) {
    cards.push(
      <div key="onshore" className="resourcing-model-selector__card">
        <span className="resourcing-model-selector__card-icon" aria-hidden="true">
          <HomeIcon width={18} height={18} />
        </span>
        <span className="resourcing-model-selector__card-body">
          <span className="resourcing-model-selector__card-label">Onshore</span>
          <span className="resourcing-model-selector__card-description">
            Delivery team members located in local timezone.
          </span>
        </span>
        <span className="resourcing-model-selector__card-pricing">
          <span className="resourcing-model-selector__card-rate">
            {Math.round(onshoreHours).toLocaleString()}h
          </span>
          <strong className="resourcing-model-selector__card-cost">
            {formatCurrency(onshoreCost)}
          </strong>
        </span>
      </div>
    );
  }

  if (offshoreHours > 0) {
    cards.push(
      <div key="offshore" className="resourcing-model-selector__card">
        <span className="resourcing-model-selector__card-icon" aria-hidden="true">
          <GlobeIcon width={18} height={18} />
        </span>
        <span className="resourcing-model-selector__card-body">
          <span className="resourcing-model-selector__card-label">Offshore</span>
          <span className="resourcing-model-selector__card-description">
            Global delivery team members in offshore centers.
          </span>
        </span>
        <span className="resourcing-model-selector__card-pricing">
          <span className="resourcing-model-selector__card-rate">
            {Math.round(offshoreHours).toLocaleString()}h
          </span>
          <strong className="resourcing-model-selector__card-cost">
            {formatCurrency(offshoreCost)}
          </strong>
        </span>
      </div>
    );
  }

  if (hybridHours > 0) {
    cards.push(
      <div key="hybrid" className="resourcing-model-selector__card">
        <span className="resourcing-model-selector__card-icon" aria-hidden="true">
          <PieChartIcon width={18} height={18} />
        </span>
        <span className="resourcing-model-selector__card-body">
          <span className="resourcing-model-selector__card-label">Hybrid</span>
          <span className="resourcing-model-selector__card-description">
            Blend of onshore and offshore delivery per role.
          </span>
        </span>
        <span className="resourcing-model-selector__card-pricing">
          <span className="resourcing-model-selector__card-rate">
            {Math.round(hybridHours).toLocaleString()}h
          </span>
          <strong className="resourcing-model-selector__card-cost">
            {formatCurrency(hybridCost)}
          </strong>
        </span>
      </div>
    );
  }

  return (
    <div className="resourcing-model-selector">
      <div className="resourcing-model-selector__header">
        <h3 className="resourcing-model-selector__title">Resourcing Model</h3>
        <p className="resourcing-model-selector__subtitle">
          Estimated project delivery staffing breakdown
        </p>
      </div>

      <div
        className="resourcing-model-selector__cards"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        {cards}
      </div>
    </div>
  );
}
